import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { Text } from '@mantine/core'
import { MoonLoader } from 'common/components'
import {
  ActionTypeEnum,
  useLoadClientAssets,
  useMessageActions,
  useSelector,
} from 'common/hooks'
import { t } from 'i18next'
import { useIFrameContext } from 'iframe/hooks'

// Dostępne statusy gotowości
enum ReadyStatusEnum {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

const HEADER_HEIGHT = 50

interface IProps {
  url: string
}

export default function IFrame({ url }: IProps) {
  const { pathname, hash, search, state } = useLocation()
  const iframeRef = useIFrameContext()
  const { title } = useSelector((state) => state.iframe)
  const { sendApprovalCloseDetailsMessage, getReadyMessage } = useMessageActions()
  const { theme } = useLoadClientAssets()
  const [isAppReady, setIsAppReady] = useState(false)

  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight - HEADER_HEIGHT,
  })

  const path = useMemo(() => pathname || url, [pathname, url])
  const iframeUrl = useMemo(() => localStorage.getItem('application_url') || '', [])
  const applicationName = useMemo(
    () => localStorage.getItem('application_name') || '',
    [],
  )

  useEffect(() => {
    if (theme) {
      window.__THEME__ = theme
    }
  }, [theme])

  useEffect(() => {
    if (!iframeRef?.current) return

    const handleMessage = (event: MessageEvent) => {
      const { type, message } = event.data
      if (type === ActionTypeEnum.READY) {
        const { status } = message || {}
        if (status === ReadyStatusEnum.ERROR) {
          // Aplikacja podrzędna napotkała błąd, ale jest "gotowa" (wyświetla komunikat o błędzie)
          setIsAppReady(true) // Ukrywamy nasz loader, pozwalając na wyświetlenie widoku błędu z aplikacji podrzędnej
        } else {
          // Standardowa obsługa sukcesu
          getReadyMessage()
          setIsAppReady(true)
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [iframeRef, getReadyMessage])

  const handleFrameLoaded = useCallback(() => {
    // Nie wysyłamy konfiguracji tutaj, czekamy na komunikat READY
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const height = window.innerHeight - HEADER_HEIGHT
      setSize({ width, height })
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    window.addEventListener('popstate', () => {
      sendApprovalCloseDetailsMessage({ state })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {!isAppReady && (
        <div
          className={
            'flex justify-center items-center gap-1 p-4 absolute w-full h-[calc(100%-60px)] bottom-0 left-0 bg-white z-[99]'
          }
        >
          <MoonLoader
            text={
              <div className={'flex items-center gap-1'}>
                <Text>{t('common.initialization')}</Text>
                <Text className={'font-semibold'}>
                  {t(`apps.${applicationName.toLowerCase().replace(/ /g, '-')}`)}
                </Text>
              </div>
            }
          />
        </div>
      )}
      <iframe
        className={'border-0'}
        height={size.height}
        key={path}
        onLoad={handleFrameLoaded}
        ref={iframeRef}
        src={iframeUrl + path + hash + search}
        width={size.width}
      />
    </>
  )
}
